import { ChangeEvent, ReactNode, useState } from "react"
import { DataTable, DataTableRowClickEventParams } from "primereact/datatable"
import { Column } from "primereact/column"
import { useUsedAddress } from "../../api/queries/useUsedAddress"
import { AddressSimple } from "../../api/types/address"
import { InputText } from "primereact/inputtext"
import { useDebounce } from "../../common/useDebounce"
import { useAddressFulltextSearch } from "../../api/queries/useAddressFulltextSearch"
import { Sidebar } from "primereact/sidebar"
import { Button } from "primereact/button"
import { useDeleteUsedAddress } from "../../api/queries/useDeleteUsedAddress"
import { countryColumnTemplate } from "../common/country"

export default function AddressBook({ isVisible, onHide, onSelect, countries }: Props) {
  const tName = (row: AddressSimple): string => row.name || ""
  const tStreetDetails = (row: AddressSimple): string => row.streetDetails || ""
  const tStreet = (row: AddressSimple): string => row.street || ""
  const tZip = (row: AddressSimple): string => row.zip || ""
  const tCity = (row: AddressSimple): string => row.city || ""
  const tCountry = (row: AddressSimple): ReactNode => countryColumnTemplate(row.country || "")
  const tPerson = (row: AddressSimple): string => row.person || ""
  const tDelete = (row: AddressSimple): ReactNode => (
    <Button icon={"pi pi-trash"} className={"p-button-sm p-button-danger p-button-text"}
      onClick={() => handleDelete(row.uuid)} />
  )

  const [search, setSearch] = useState<string>("")
  const searchDebounced = useDebounce(search, 500)
  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearch(event.target.value)
  }
  const tHeader = <InputText value={search} onChange={handleSearchChange}
    placeholder={"Vyhledat"} className={"w-full"} />

  const {
    data: addresses,
    isLoading: isUsedAddressLoading,
    refetch: refetchUsedAddress,
  } = useUsedAddress()
  const {
    data: fulltext,
    isLoading: isFulltextSearchLoading,
    refetch: refetchFulltextSearch,
  } = useAddressFulltextSearch(searchDebounced)

  const deleteUsedAddress = useDeleteUsedAddress()

  const data = fulltext?.results.addressList
    ? fulltext?.results.addressList
    : addresses?.results.addressList

  const onRowClick = (row: DataTableRowClickEventParams) => onSelect(row.data)

  const handleDelete = async (uuid: string) => {
    if (window.confirm("Opravdu chcete smazat adresu?")) {

      await deleteUsedAddress.mutateAsync(uuid)

      if (search) {
        refetchFulltextSearch()
      } else {
        refetchUsedAddress()
      }
    }
  }

  const rowClassName = () => ({
    "cursor-pointer": true
  })

  const filterAddress = (address: AddressSimple) => {
    if (countries) {
      return countries.indexOf(address.country) > -1
    }

    return true
  }

  return (
    <Sidebar visible={isVisible} onHide={onHide} position={"right"} style={{ width: "90%" }} >
      <div className={"col-12"}>
        <DataTable
          emptyMessage={"Žádné záznamy"} className={"p-datatable-sm"}
          loading={isUsedAddressLoading || isFulltextSearchLoading}
          rowHover={true} stripedRows={true} header={tHeader}
          onRowClick={onRowClick} rowClassName={rowClassName}
          value={(data || []).filter(filterAddress)}
        >
          <Column header={"Název"} body={tName} />
          <Column header={"Upřesnění"} body={tStreetDetails} />
          <Column header={"Ulice a číslo domu"} body={tStreet} />
          <Column header={"PSČ"} body={tZip} />
          <Column header={"Město"} body={tCity} />
          <Column header={"Stát"} body={tCountry} />
          <Column header={"Jméno kontaktní osoby"} body={tPerson} />
          <Column body={tDelete} />
        </DataTable>
      </div>
    </Sidebar>
  )
}

export interface Props {
  isVisible: boolean;
  onHide: () => void;
  onSelect: (address: AddressSimple) => void;
  countries?: string[];
}
